import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Avatar,
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import {
  apiCallVenueList,
  apiCallVenueStatusUpdate,
  apiCallVenueStatusDelete,
  apiCallGetLocaitonList,
} from "../../../Core/Apicall";
import { useNavigate, useSearchParams } from "react-router-dom";
import DeletePopup from "../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../Common/CommonStatus/CommonStatus";
import CommonPagination from "../../Common/CommonPagination/CommonPagination";
import CommonShimmer from "../../Common/CommonShimmer/CommonShimmer";
import EmptyData from "../../Common/EmptyData/EmptyData";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import { usePaginationState } from "../../../hooks/usePaginationState";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";

interface Venue {
  _id: string;
  venueName: string;
  zipCode: string;
  country: string;
  stateOrRegion: string;
  cityOrCounty: string;
  latitude: number;
  longitude: number;
  phoneNumber: string;
  phoneCountryCode: string;
  venueType: string;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

function Venues() {
  const [data, setData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVenue, setMenuVenue] = useState<Venue | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const navigate = useNavigate();
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const [selectedMediaForPreview, setSelectedMediaForPreview] = useState<
    any | null
  >(null);


  const [locations, setLocations] = useState<any[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const {
    page,
    search,
    setSearch,
    debouncedSearch,
    handlePageChange,
    setPage,
    country,
    setCountry,
    state,
    setState,
    city,
    setCity
  } = usePaginationState();


  useEffect(() => {
    fetchLocations()
  }, [])

  const fetchLocations = async () => {
    try {
      const res = await apiCallGetLocaitonList();
      if (res?.data) {
        const urlCountry = searchParams.get("country");
        const urlState = searchParams.get("state");
        setLocations(res.data);
        setCountries(res.data.map((c: any) => c.country));
        if (urlCountry) {
          const selectedCountry = res.data.find((c: any) => c.country === urlCountry);
          setStates(selectedCountry?.states || []);

          if (urlState) {
            const selectedState = selectedCountry?.states.find((s: any) => s.state === urlState);
            setCities(selectedState?.cities || []);
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchVenues = async () => {
    if (loading) return
    setLoading(true);
    try {

      const response = await apiCallVenueList({
        search:  debouncedSearch,
        country:  country,
        state:  state,
        city:  city,
        offset: page,
        limit: rowsPerPage,
      });
      if (response?.data) {
        setData(response.data);
        setTotalItems(response.totalCount || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
      fetchVenues();
  }, [page, rowsPerPage, debouncedSearch, country, state, city]);


  const handleStatusUpdate = async (venue: Venue) => {
    try {
      await apiCallVenueStatusUpdate({
        venueId: venue._id,
        isActive: !venue.isActive,
      });
      setData((prev) =>
        prev.map((item) =>
          item._id === venue._id ? { ...item, isActive: !venue.isActive } : item
        )
      );
      setStatusDialogOpen(false);
    } catch (err) { }
  };
  const handleDelete = async () => {
    if (!menuVenue) return;
    try {
      await apiCallVenueStatusDelete({
        venueId: menuVenue._id,
        isDelete: true,
      });
      setData((prev) => prev.filter((item) => item._id !== menuVenue._id));
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false);
      setMenuVenue(null);
    }
  };
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    venue: Venue
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuVenue(venue);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVenue) {
          navigate("/admin/VenueUpdate", { state: { venue: menuVenue } });
        }
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    },
  ];

  const onClickVenue = (item: any) => {
    navigate("/admin/VenueDetails", {
      state: {
        venue: item,
        backPage: page,
      },
    });
  };


  return (
    <Box style={{ paddingBottom: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          padding: "12px 16px",
          background: "#FFFF",
          borderRadius: "12px",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* Left Filters */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search venue..."
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value) }}
            sx={{
              width: 280,
              background: "#fff",
              borderRadius: "8px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Country */}
          <Autocomplete
            size="small"
            options={countries}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            value={country}
            onChange={(e, value) => {
              setCountry(value);
              setState(null);
              setCity(null);
              setPage(1);
              const selectedCountry = locations.find(
                (c: any) => c.country === value
              );
              setStates(selectedCountry?.states || []);
              setCities([]);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="Country" />
            )}
          />

          <Autocomplete
            size="small"
            options={states.map((s: any) => s.state)}
            value={state}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            onChange={(e, value) => {
              setState(value);
              setCity(null);
              setPage(1);
              const selectedState = states.find(
                (s: any) => s.state === value
              );

              setCities(selectedState?.cities || []);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="State" />
            )}
          />

          <Autocomplete
            size="small"
            options={cities}
            value={city}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            onChange={(e, value) => {
              setCity(value);
              setPage(1);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="City" />
            )}
          />
        </Box>

        {/* Right Button */}
        <Button
          onClick={() => navigate("/admin/Add-Venue")}
          variant="contained">

          Create New
        </Button>
      </Box>
      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          height: "auto",
          position: "relative",
        }}
      >
        <TableContainer sx={{ height: "auto" }} className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Venue</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>City</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            {loading === false && (
              <TableBody>
                {data.map((item: any, index: any) => (
                  <TableRow hover key={item._id}>
                    <TableCell>
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          gap: "10px",
                        }}
                      >
                        {item?.coverImage?.url ? (
                          <Avatar
                            src={item.coverImage?.url}
                            alt="Profile"
                            sx={{ width: 50, height: 50, cursor: "pointer" }}
                            onClick={() =>
                              setSelectedMediaForPreview({
                                url: item.coverImage?.url,
                              })
                            }
                          />
                        ) : (
                          "-"
                        )}
                        <div
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={() => onClickVenue(item)}
                        >
                          {item?.venueName?.length > 11
                            ? item?.venueName.substring(0, 10) + "..."
                            : item?.venueName}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => onClickVenue(item)}
                    >
                      {item?.venueType}
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => onClickVenue(item)}
                    >
                      {item?.phoneCountryCode} {item?.phoneNumber}
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => onClickVenue(item)}
                    >
                      {item?.cityOrCounty}
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => onClickVenue(item)}
                    >
                      {item?.stateOrRegion}
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => onClickVenue(item)}
                    >
                      {item?.country}
                    </TableCell>
                    <TableCell>
                      <Button
                        className={
                          item?.isActive ? "btn-success" : "btn-danger"
                        }
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item?.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.venueName}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"
                          } this venue?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box className="d-flex align-items-center gap-1">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate("/admin/Analytics", {
                              state: { venueId: item._id },
                            })
                          }
                        >
                          <AnalyticsOutlinedIcon fontSize="small" />
                        </IconButton>

                        <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                          <MoreHoriz />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {loading && (
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>
          )}
        </TableContainer>
        {loading === false && <EmptyData data={data} />}
        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />
      </Paper>
      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />
      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this venue?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Dialog
        open={!!selectedMediaForPreview}
        onClose={() => setSelectedMediaForPreview(null)}
      >
        <Avatar
          src={selectedMediaForPreview?.url}
          variant="rounded"
          sx={{ width: 300, height: "auto" }}
        />
      </Dialog>
    </Box>
  );
}

export default Venues;
